import * as React from "react";
import { Zap, ChevronRight } from "lucide-react";
import { ExecutionSummary } from "../../../../types/navigationTypes";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ProjectCardProps {
  execution: ExecutionSummary['execution'];
  onClick: () => void;
  compact?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ execution, onClick, compact = false }) => {
  const mapStateIdToStatus = (stateId: string): string => {
    const statusMap: Record<string, 'active' | 'completed' | 'error' | 'pending'> = {
      'state-1': 'active',
      'state-2': 'completed',
      'state-3': 'error',
      'state-4': 'pending'
    };
    return statusMap[stateId] || 'pending';
  };

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'active': return "bg-emerald-50 text-emerald-700 border-transparent hover:bg-emerald-100";
      case 'completed': return "bg-blue-100 text-blue-800 border-transparent hover:bg-blue-200";
      case 'error': return "bg-red-100 text-red-700 border-transparent hover:bg-red-200";
      default: return "bg-amber-100 text-amber-800 border-transparent hover:bg-amber-200";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'error': return 'Error';
      default: return 'Pending';
    }
  };

  const status = mapStateIdToStatus(execution.state_id);

  if (compact) {
    return (
      <div 
        className={cn(
          "flex items-center p-3 gap-3 w-full transition-colors",
          // Styles are handled by parent mostly, but we add base layout
        )}
        onClick={onClick}
      >
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Zap className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <h3 className="text-sm font-medium text-foreground truncate m-0">
              {execution.name}
            </h3>
            <span className="text-[10px] text-muted-foreground shrink-0">
               {new Date(execution.created_at).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
            </span>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={cn(
              "inline-block w-2 h-2 rounded-full", 
              status === 'active' ? "bg-emerald-500" : 
              status === 'completed' ? "bg-blue-500" :
              status === 'error' ? "bg-red-500" : "bg-amber-500"
            )} />
            <span className="truncate">{execution.workpill?.name || 'WorkPill'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card 
      className={cn(
        "flex items-center p-5 cursor-pointer transition-all relative group",
        "hover:-translate-y-1 hover:shadow-lg hover:border-primary/20"
      )}
      onClick={onClick}
    >
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mr-5 shrink-0">
        <Zap size={24} />
      </div>
      
      <div className="flex-1 flex flex-col gap-1 min-w-0">
        <h3 className="text-lg font-semibold text-foreground m-0 whitespace-nowrap overflow-hidden text-ellipsis">
          {execution.name}
        </h3>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Badge 
            variant="outline"
            className={cn("uppercase tracking-wide font-bold px-2 py-0.5", getStatusClasses(status))}
          >
            {getStatusLabel(status)}
          </Badge>
          <span>•</span>
          <span>{execution.workpill?.name || 'WorkPill'}</span>
        </div>
      </div>
      
      <div className="ml-auto flex items-center gap-6 pl-5">
        <div className="flex flex-col items-end gap-1 min-w-[100px]">
          <span className="text-sm font-medium text-muted-foreground">Created</span>
          <span className="text-base font-semibold text-primary">
            {new Date(execution.created_at).toLocaleDateString('es-ES', {
              month: 'short',
              day: 'numeric'
            })}
          </span>
        </div>
        <div className="w-8 h-8 flex items-center justify-center text-muted-foreground transition-colors hover:text-primary">
          <ChevronRight size={20} />
        </div>
      </div>
    </Card>
  );
};

export default ProjectCard;
